import clsx from 'clsx';
import { Container, Grid, Typography } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { ActivePane, useAppStateContext } from '../AppStateProvider/AppStateProvider';
import Logo from '../Header/net2phone.png';
import { AvatarComponent } from 'n2p-ui-library';
import { useMemo, useState } from 'react';
import { Popover } from 'n2p-ui-library';
import classnames from 'classnames';
import { logout } from '../AppStateProvider/authentification/authentification';
import { HistoryResultsModal } from '../panes/Results/HistoryResults/HistoryResultsModal';
import HeaderSmallItemMenu from './HeaderSmallItemMenu';
import { t } from 'i18next';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    header: {
      position: 'absolute',
      left: 0,
      right: 0,
      top: 0,
      bottom: `calc(100% - ${theme.navHeight}px)`,
      justifyContent: 'center',
      zIndex: 100,
      background: 'inherit',
    },
    headerTypography: {
      variant: 'body1',
      color: 'white',
      fontWeight: 'bold',
      fontSize: 'medium',
      '@media screen and (max-width: 300px)': {
        fontSize: '16px',
        marginRight: '10px',
      },
    },
    breadcrumb: {
      fontWeight: 600,
      color: '#AEB2C1',
      borderBottom: '2px solid #e6e6e6',
      display: 'flex',
      height: '100%',
      flex: '1',
      transition: 'borderBottom 1s ease',
      '&:last-child svg': {
        display: 'none',
      },
      '@media screen and (max-width: 950px)': {
        borderBottom: 'none',
      },
    },
    smallText: {
      fontSize: '10px',
    },
    active: {
      color: theme.typography.body1.color,
      borderBottom: '2px solid #3694ff',
      '@media screen and (max-width: 950px)': {
        borderBottom: 'none',
      },
    },
    progressBar: {
      position: 'absolute',
      left: 0,
      right: 0,
      bottom: 0,
      top: 'calc(100% - 2px)',
      background: '#CACDD8',
    },
    progressBarForeground: {
      background: theme.palette.primary.main,
      width: '0',
      height: '100%',
      transition: 'width 1s ease',
    },
    logo: {
      margin: '0 30px',
      width: '105px',
      height: '27px',
      '@media screen and (max-width: 300px)': {
        marginLeft: '10px',
      },
    },
    gridContainer: {
      height: '50%',
      backgroundColor: '#002540',
      '@media screen and (max-width: 300px)': {
        display: 'flex',
        justifyContent: 'space-between',
      },
    },
    headerAvatar: {
      width: '60px',
      height: '50px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      marginRight: '-2px',
      '&hover': {
        backgroundColor: 'var(--color-primary-light-1)',
      },
    },
    headerContainer: {
      position: 'relative',
      marginLeft: 'auto',
      marginRight: '0px',
    },
    headerAvatarGeneral: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      height: 'var(--spog-header-height)',
      color: 'var(--color-white)',
    },
    headerProfileMenu: {
      width: '115px',
      listStyle: 'none',
    },

    headerProfileMenuItem: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-start',
      height: '36px',
      padding: '0 0px',
    },
  })
);

export const getActivePaneInfo = (activePane: ActivePane) => {
  const paneMapping = {
    0: { pane: ActivePane.GetStarted, label: t('HEADER.GET_STARTED'), paneNumber: 1 },
    1: { pane: ActivePane.DeviceCheck, label: t('HEADER.1_DEVICE_&_SOFTWARE_SETUP'), paneNumber: 1 },
    2: { pane: ActivePane.DeviceError, label: t('HEADER.1_DEVICE_&_SOFTWARE_SETUP'), paneNumber: 1 },
    3: { pane: ActivePane.CameraTest, label: t('HEADER.1_DEVICE_&_SOFTWARE_SETUP'), paneNumber: 1 },
    4: { pane: ActivePane.SpeakersTest, label: t('HEADER.1_DEVICE_&_SOFTWARE_SETUP'), paneNumber: 1 },
    5: { pane: ActivePane.AudioTest, label: t('HEADER.1_DEVICE_&_SOFTWARE_SETUP'), paneNumber: 1 },
    6: { pane: ActivePane.BrowserTest, label: t('HEADER.1_DEVICE_&_SOFTWARE_SETUP'), paneNumber: 1 },
    7: { pane: ActivePane.Connectivity, label: t('HEADER.2_CONNECTIVITY'), paneNumber: 2 },
    8: { pane: ActivePane.CodecsTest, label: t('HEADER.3_AUDIO/VIDEO_CODECS'), paneNumber: 3 },
    9: { pane: ActivePane.Quality, label: t('HEADER.4_QUALITY_&_PERFORMANCE'), paneNumber: 4 },
    10: { pane: ActivePane.Results, label: t('HEADER.5_RESULTS'), paneNumber: 5 },
  };

  return paneMapping[activePane] || { pane: null, label: t('HEADER.UNKNOWN_PANE'), paneNumber: 0 };
};

function HeaderItem({ label, pane, paneNumber }: { label: string; pane: ActivePane; paneNumber: number }) {
  const classes = useStyles();
  const { state, dispatch } = useAppStateContext();
  const getStartedIsActivePane = state.activePane === ActivePane.GetStarted;
  const isLongText = (text: string) => text.length > 25;

  return (
    <div
      className={clsx(classes.breadcrumb, {
        [classes.active]: state.activePane >= pane,
        [classes.smallText]: isLongText(label),
      })}
      style={{ display: 'flex', alignItems: 'center' }}
    >
      <button
        disabled={getStartedIsActivePane}
        onClick={
          paneNumber === 1
            ? () =>
                dispatch({
                  type: 'set-active-pane',
                  newActivePane: ActivePane.CameraTest,
                })
            : () => dispatch({ type: 'set-active-pane', newActivePane: pane })
        }
        style={{ display: 'flex', pointerEvents: getStartedIsActivePane ? 'none' : 'initial' }}
      >
        {state.deviceTestIsNotFinished && paneNumber === 1 ? (
          <Grid container alignItems="center">
            <div style={{ display: 'flex', alignItems: 'center', marginRight: '10px' }}>
              <span
                style={{ width: '24px', height: '52px', backgroundColor: '#F44349' }}
                className="n2pc-icon n2pc-icon-alert-filled"
              />
              <Typography
                variant="body1"
                style={{
                  color: '#F44349',
                  fontWeight: 600,
                  marginLeft: '5px',
                }}
              >
                {label}
              </Typography>
            </div>
          </Grid>
        ) : (
          <Grid container alignItems="center" style={{ flexWrap: 'nowrap' }}>
            <div
              className="n2pc-circle-icon-wrapper primary"
              style={{
                marginRight: '10px',
                backgroundColor: 'transparent',
                border: '2px solid #3694ff',
              }}
            >
              <span>
                <Typography
                  variant="body1"
                  style={{
                    color: '#3694ff',
                    fontWeight: 600,
                  }}
                >
                  {paneNumber}
                </Typography>
              </span>
            </div>
            <Typography
              variant="body1"
              style={{
                color: '#3694ff',
                fontWeight: 600,
                width: isLongText(label) ? '170px' : 'auto',
              }}
            >
              {label}
            </Typography>
          </Grid>
        )}
      </button>
    </div>
  );
}

function HeaderItemSmall() {
  const classes = useStyles();
  const { state } = useAppStateContext();
  // eslint-disable-next-line
  const [isHover, setIsHover] = useState(false);
  const handleMouseEnter = () => {
    setIsHover(true);
  };

  const handleMouseLeave = () => {
    setIsHover(false);
  };

  const firstLetter: any = localStorage.getItem('firstLetter');
  const [open, setOpen] = useState(false);
  const activePaneInfo = getActivePaneInfo(state.activePane);
  const shouldRenderSmallMenu = ![ActivePane.GetStarted, ActivePane.DeviceCheck, ActivePane.DeviceError].includes(
    activePaneInfo.pane
  );

  function handleOpen() {
    setOpen(true);
  }
  const renderAvatarComponent = useMemo((): JSX.Element => {
    return (
      <div className={classes.headerAvatar} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
        <AvatarComponent fontSize={12} onlyFirstLetter size={28} value={firstLetter !== null ? firstLetter : 'L'} />
      </div>
    );
  }, [classes.headerAvatar, firstLetter]);

  return (
    <div className={classes.header}>
      <Grid container alignItems="center" justifyContent="flex-start" className={classes.gridContainer}>
        <img src={Logo} className={classes.logo} alt={'logo'} />
        <Typography className={classes.headerTypography}>Diagnostics App</Typography>
        {localStorage.getItem('token') !== null && localStorage.getItem('token') !== 'undefined' ? (
          <Grid style={{ marginLeft: 'auto' }}>
            <div className={classes.headerAvatarGeneral}>
              <div className={classnames(classes.headerContainer, classes.headerAvatar)}>
                <Popover
                  trigger="click"
                  position="bottom"
                  disableScroll
                  offset={3}
                  contentStyles={{
                    animationName: 'fadeInUp',
                    animationDuration: '.3s',
                    borderRadius: 6,
                    marginLeft: '-15px',
                    marginTop: '-3px',
                  }}
                  arrow={false}
                  activatorControlled={renderAvatarComponent}
                >
                  <ul className={classes.headerProfileMenu} style={{ marginLeft: '0px' }}>
                    <li className={classes.headerProfileMenuItem} style={{ paddingTop: '15px' }}>
                      <button type="button" className="n2pc-btn-text" onClick={handleOpen}>
                        {t('HEADER.HISTORY_RESULTS')}
                      </button>
                      <HistoryResultsModal
                        isModalOpen={open}
                        setIsModalOpen={setOpen}
                        historyResults={state.historyResults}
                      />
                    </li>
                    <li className={classes.headerProfileMenuItem} style={{ marginTop: '15px' }}>
                      <button type="button" className="n2pc-btn-text" onClick={logout}>
                        {t('HEADER.SIGN_OUT')}
                      </button>
                    </li>
                  </ul>
                </Popover>
              </div>
            </div>
          </Grid>
        ) : null}
      </Grid>
      <Container
        style={{
          width: '100%',
          height: '50px',
          display: 'flex',
          padding: '0px',
          paddingLeft: '3px',
          paddingRight: '3px',
        }}
      >
        <Grid container alignItems="center" justifyContent="space-between" style={{ height: '100%', gap: '3px' }}>
          <HeaderItem pane={activePaneInfo.pane} label={activePaneInfo.label} paneNumber={activePaneInfo.paneNumber} />
          {shouldRenderSmallMenu && <HeaderSmallItemMenu />}
        </Grid>
      </Container>
    </div>
  );
}

function HeaderItemsLarge() {
  const classes = useStyles();
  const { state } = useAppStateContext();
  // eslint-disable-next-line
  const [isHover, setIsHover] = useState(false);

  const handleMouseEnter = () => {
    setIsHover(true);
  };

  const handleMouseLeave = () => {
    setIsHover(false);
  };

  const firstLetter: any = localStorage.getItem('firstLetter');
  const [open, setOpen] = useState(false);

  function handleOpen() {
    setOpen(true);
  }

  const panesToDisplay = [
    ActivePane.DeviceCheck,
    ActivePane.Connectivity,
    ActivePane.CodecsTest,
    ActivePane.Quality,
    ActivePane.Results,
  ];

  const renderAvatarComponent = useMemo((): JSX.Element => {
    return (
      <div className={classes.headerAvatar} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
        <AvatarComponent fontSize={12} onlyFirstLetter size={28} value={firstLetter !== null ? firstLetter : 'L'} />
      </div>
    );
  }, [classes.headerAvatar, firstLetter]);
  return (
    <div className={classes.header}>
      <Grid
        container
        alignItems="center"
        justifyContent="flex-start"
        style={{ height: '50%', backgroundColor: '#002540' }}
      >
        <img src={Logo} style={{ margin: '0 30px', width: '105px', height: '27px' }} alt={'logo'} />
        <Typography className={classes.headerTypography}>Diagnostics App</Typography>
        {localStorage.getItem('token') !== null && localStorage.getItem('token') !== 'undefined' ? (
          <Grid style={{ marginLeft: 'auto' }}>
            <div className={classes.headerAvatarGeneral}>
              <div className={classnames(classes.headerContainer, classes.headerAvatar)}>
                <Popover
                  trigger="click"
                  position="bottom"
                  disableScroll
                  offset={3}
                  contentStyles={{
                    animationName: 'fadeInUp',
                    animationDuration: '.3s',
                    borderRadius: 6,
                    marginLeft: '-15px',
                    marginTop: '-3px',
                  }}
                  arrow={false}
                  activatorControlled={renderAvatarComponent}
                >
                  <ul className={classes.headerProfileMenu}>
                    <li className={classes.headerProfileMenuItem} style={{ paddingTop: '15px' }}>
                      <button
                        type="button"
                        className="n2pc-btn-text"
                        style={{ marginLeft: '0px', textAlign: 'left' }}
                        onClick={handleOpen}
                      >
                        {t('HEADER.HISTORY_RESULTS')}
                      </button>
                      <HistoryResultsModal
                        isModalOpen={open}
                        setIsModalOpen={setOpen}
                        historyResults={state.historyResults}
                      />
                    </li>
                    <li className={classes.headerProfileMenuItem} style={{ marginTop: '15px' }}>
                      <button type="button" className="n2pc-btn-text" onClick={logout}>
                        {t('HEADER.SIGN_OUT')}
                      </button>
                    </li>
                  </ul>
                </Popover>
              </div>
            </div>
          </Grid>
        ) : null}
      </Grid>
      <Container
        style={{
          width: '100%',
          height: '50px',
          display: 'flex',
          padding: '0px',
          paddingLeft: '3px',
          paddingRight: '3px',
        }}
      >
        <Grid container alignItems="center" justifyContent="space-between" style={{ height: '100%', gap: '3px' }}>
          {panesToDisplay.map((pane) => {
            const paneInfo = getActivePaneInfo(pane);
            return (
              <HeaderItem key={pane} pane={paneInfo.pane} label={paneInfo.label} paneNumber={paneInfo.paneNumber} />
            );
          })}
        </Grid>
      </Container>
    </div>
  );
}

export default function Header() {
  const large = window.innerWidth > 950;
  return large ? <HeaderItemsLarge></HeaderItemsLarge> : <HeaderItemSmall></HeaderItemSmall>;
}
